// /app/frontend/src/pages/seo/PolyDatingForCouplesPage.jsx
// Audience: established couples exploring polyamory together.
import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import {
  SEOHead,
  organizationSchema,
  mobileAppSchema,
  buildBreadcrumbSchema,
  GradientHero,
  FeatureCard,
  StepCard,
  RelatedGuides,
  BottomCTA,
} from './SEOUtils';

export default function PolyDatingForCouplesPage() {
  const path = '/poly-dating-for-couples';
  const schemas = [
    organizationSchema,
    mobileAppSchema,
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Guides', url: '/guides' },
      { name: 'Poly Dating For Couples', url: path },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="Poly Dating For Couples"
        description="Explore ethical non-monogamy, poly relationships, and family-building opportunities designed for couples seeking meaningful connections."
        canonical={path}
        schemas={schemas}
      />

      <GradientHero
        eyebrow="For Established Couples"
        headline="Poly Dating For Couples Seeking More"
        sub="Whether you're brand new to ENM or already living it, UnityLove gives established couples the tools to date as a unit — honestly, safely, and without explaining yourselves."
        body="Linked couple profiles, shared preferences, and a community of people who understand consensual non-monogamy from the start."
        primaryCta={{ label: 'Create A Couple Profile', to: '/auth?mode=signup' }}
        secondaryCta={{ label: 'Ethical Polyamory For Beginners', to: '/ethical-polyamory-for-beginners' }}
      />

      <main className="max-w-4xl mx-auto px-4 py-14">
        {/* Intro */}
        <section className="mb-12 max-w-2xl mx-auto text-center">
          <p className="text-lg text-slate-700 leading-relaxed">
            Most apps weren’t built with couples in mind. UnityLove was. Two profiles can be linked, your preferences can be shared, and the people you meet know from the very first message that you’re a unit — not a secret.
          </p>
        </section>

        {/* What couples actually need */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-6 text-center">
            What Couples Actually Need
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <FeatureCard
              icon="🔗"
              accent="rose"
              title="Couple Profiles"
              body="Link two accounts so you can show up as a couple wherever you both want to. Match preferences and conversations can be shared from day one."
            />
            <FeatureCard
              icon="🤝"
              accent="emerald"
              title="Shared Matching"
              body="Filter together for the kind of connection you're looking for — third partner, separate dating, family-building, casual friendships, or anything in between."
            />
            <FeatureCard
              icon="💬"
              accent="sky"
              title="Honest Communication Tools"
              body="Profile prompts, value tags, and structured first messages help skip the awkward 'are we both on the same page' phase that derails so many early connections."
            />
            <FeatureCard
              icon="🚧"
              accent="amber"
              title="Clear Boundaries"
              body="Couples can publish their boundaries up front — what's open, what's closed, what the rhythm of dating looks like — so matches arrive informed."
            />
            <FeatureCard
              icon="👶"
              accent="violet"
              title="Family-Building Options"
              body="If your couple is exploring family-building (sister wives, triads, plural family), our community includes singles whose long-term vision matches yours."
            />
            <FeatureCard
              icon="🛡️"
              accent="emerald"
              title="Verified Members"
              body="Identity verification (via Stripe Identity) helps protect both partners from catfishing, fake couples, and bad actors — a real risk in couple-dating spaces."
            />
          </div>
        </section>

        {/* Common structures */}
        <section className="mb-14 bg-slate-50 rounded-3xl p-6 sm:p-8">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
            Common Structures Couples Explore
          </h2>
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              <strong>Hierarchical poly:</strong> One primary couple, with additional partners who are loved and respected but not central to household and life decisions.
            </p>
            <p>
              <strong>Triad / closed throuple:</strong> Three people in a closed romantic relationship — sometimes evolving from a couple meeting a third, sometimes intentionally formed.
            </p>
            <p>
              <strong>Polyamory with separate partners:</strong> Each partner in the couple dates and connects with their own additional partners, often without significant overlap.
            </p>
            <p>
              <strong>Plural family / sister wives:</strong> A long-term family structure with multiple committed wives sharing life with a husband. See our <a href="/how-to-find-a-sister-wife" className="text-rose-600 hover:text-rose-700 font-medium">sister wife guide</a> for more.
            </p>
            <p>
              <strong>Open relationships:</strong> Sexually non-monogamous, with the romantic core remaining the couple. Sometimes evolves toward polyamory, sometimes not.
            </p>
            <p className="text-sm text-slate-600 italic">
              None of these is more valid than another. The right structure is the one all parties consciously consent to.
            </p>
          </div>
        </section>

        {/* How to start */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-6">
            How To Start As A Couple
          </h2>
          <div className="grid gap-3">
            <StepCard n={1} title="Have the conversation first" body="Before any app, talk through what you both actually want — not what one of you wants and the other is agreeing to. Both partners need genuine enthusiasm for this to work." />
            <StepCard n={2} title="Define your structure & boundaries" body="Hierarchical or non-hierarchical? Sexually open or closed? How is time managed? Write it down together so nothing's left to assumption." />
            <StepCard n={3} title="Create linked profiles" body="On UnityLove, you can link as a couple. Both partners participate in conversations from the start — no surprises, no hidden DMs." />
            <StepCard n={4} title="Communicate often, especially early" body="The first few months of dating as a couple stir up everything. Schedule regular check-ins between partners, not just dates outside the relationship." />
            <StepCard n={5} title="Move slow" body="The healthiest poly families take their time. There's no race. The faster you rush, the more likely something gets skipped." />
          </div>
        </section>

        {/* Related guides */}
        <section className="mb-4">
          <h2 className="font-playfair text-2xl font-bold text-slate-900 mb-4">
            Read Next
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <a href="/ethical-polyamory-for-beginners" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Ethical Polyamory For Beginners</p>
              <p className="text-sm text-slate-600 mt-1">The foundations of consent, communication, and care.</p>
            </a>
            <a href="/safe-poly-dating" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Safe Poly Dating Guide</p>
              <p className="text-sm text-slate-600 mt-1">Practical safety tips for poly relationships.</p>
            </a>
            <a href="/dating-as-a-couple" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Dating As A Couple</p>
              <p className="text-sm text-slate-600 mt-1">Navigate shared dating experiences together.</p>
            </a>
            <a href="/how-to-find-a-sister-wife" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">How To Find A Sister Wife</p>
              <p className="text-sm text-slate-600 mt-1">A respectful, step-by-step guide.</p>
            </a>
          </div>
        </section>
      </main>

      <RelatedGuides currentPath={path} />
      <BottomCTA />
    </div>
  );
}
